import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';
import './VerificationDetail.css';

const PLATFORMS = ['nick', 'volto', 'volto_slate', 'volto_vlt', 'plone'];
const RESULTS = ['all', 'pass', 'conditional', 'fail', 'changed', 'unverified'];

const isStale = (row) => {
  if (!row.verified_at || !row.translation_updated_at) return false;
  return new Date(row.translation_updated_at) > new Date(row.verified_at);
};

const resultLabel = (r, stale) => {
  if (!r) return { text: 'unverified', cls: 'vd-badge--grey' };
  if (stale) return { text: 'changed since', cls: 'vd-badge--amber' };
  if (r === 'pass') return { text: 'pass', cls: 'vd-badge--green' };
  if (r === 'conditional') return { text: 'conditional', cls: 'vd-badge--yellow' };
  if (r === 'fail') return { text: 'fail', cls: 'vd-badge--red' };
  return { text: r, cls: 'vd-badge--grey' };
};

const VerificationDetail = () => {
  const { code } = useParams();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [langName, setLangName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platform, setPlatform] = useState('');
  const [resultFilter, setResultFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  // Fetch ALL items for this language (no pagination — single request)
  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ language: code, page_size: 10000 });
    if (platform) params.set('platform', platform);

    fetchApi(`${getApiPath()}/@polyglot/verification?${params}`)
      .then((json) => {
        setItems(json.items || []);
        setSummary(json.summary || []);
        if (json.language_name) setLangName(json.language_name);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [code, platform]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setExpandedRow(null); }, [platform, resultFilter, search]);

  // Client-side filtering (result + search)
  const filtered = useMemo(() => {
    let rows = items;
    if (resultFilter !== 'all') {
      if (resultFilter === 'unverified') {
        rows = rows.filter((r) => !r.result);
      } else if (resultFilter === 'changed') {
        rows = rows.filter((r) => r.result && isStale(r));
      } else {
        rows = rows.filter((r) => r.result === resultFilter && !isStale(r));
      }
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.english_source || '').toLowerCase().includes(q) ||
          (r.msgstr || '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [items, resultFilter, search]);

  // Summary for active platform (or all)
  const activeSummary = useMemo(() => {
    const rows = platform ? summary.filter((s) => s.platform === platform) : summary;
    return rows.reduce(
      (acc, row) => ({
        total: acc.total + parseInt(row.total_translated || 0, 10),
        verified: acc.verified + parseInt(row.verified || 0, 10),
        pass: acc.pass + parseInt(row.pass || 0, 10),
        conditional: acc.conditional + parseInt(row.conditional || 0, 10),
        fail: acc.fail + parseInt(row.fail || 0, 10),
        unverified: acc.unverified + parseInt(row.unverified || 0, 10),
      }),
      { total: 0, verified: 0, pass: 0, conditional: 0, fail: 0, unverified: 0 },
    );
  }, [summary, platform]);

  if (error) {
    return (
      <div className="vd-page">
        <div className="vd-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="vd-page">
      <Helmet title={`Verification: ${langName || code} - Polyglot`} />
      <Link to="/polyglot/coverage" className="vd-back">&larr; Back to Coverage</Link>

      <h1 className="vd-title">Verification: {langName ? `${langName} (${code})` : code}</h1>

      {/* Summary badges — scoped to active platform */}
      {summary.length > 0 && (
        <div className="vd-summary">
          <span className="vd-badge vd-badge--green">{activeSummary.pass} pass</span>
          <span className="vd-badge vd-badge--yellow">{activeSummary.conditional} conditional</span>
          <span className="vd-badge vd-badge--red">{activeSummary.fail} fail</span>
          <span className="vd-badge vd-badge--grey">{activeSummary.unverified} unverified</span>
          <span className="vd-summary__total">
            {activeSummary.total} strings
          </span>
        </div>
      )}

      {/* Platform tabs */}
      <div className="vd-tabs">
        <button
          className={`vd-tab${platform === '' ? ' vd-tab--active' : ''}`}
          onClick={() => setPlatform('')}
        >
          All Platforms
        </button>
        {PLATFORMS.map((p) => {
          const ps = summary.find((s) => s.platform === p);
          if (!ps) return null;
          return (
            <button
              key={p}
              className={`vd-tab${platform === p ? ' vd-tab--active' : ''}`}
              onClick={() => setPlatform(p)}
            >
              {p.replace('_', ' ')}
            </button>
          );
        })}
      </div>

      {/* Filters row */}
      <div className="vd-filters">
        <div className="vd-filter-group">
          {RESULTS.map((r) => (
            <button
              key={r}
              className={`vd-filter-btn${resultFilter === r ? ' vd-filter-btn--active' : ''}`}
              onClick={() => setResultFilter(r)}
            >
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>
        <input
          className="vd-search"
          type="text"
          placeholder="Search English or translation..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="vd-filter-count">{filtered.length} strings</span>
      </div>

      {loading ? (
        <p className="vd-loading">Loading verification data...</p>
      ) : (
        <div className="vd-table-wrap">
          <table className="vd-table">
            <thead>
              <tr>
                <th className="vd-th vd-th--id">ID</th>
                <th className="vd-th vd-th--english">English Source</th>
                <th className="vd-th vd-th--translation">Translation</th>
                <th className="vd-th vd-th--result">Result</th>
                <th className="vd-th vd-th--findings">Findings</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, idx) => {
                const stale = isStale(row);
                const rl = resultLabel(row.result, stale);
                const isExpanded = expandedRow === idx;
                return (
                  <tr key={`${row.message_id}-${row.platform}`} className="vd-row">
                    <td className="vd-td vd-td--id">
                      <Link to={`/polyglot/messages/${row.message_id}?language=${code}`}>
                        {row.message_id}
                      </Link>
                    </td>
                    <td className="vd-td vd-td--english">{row.english_source}</td>
                    <td className="vd-td vd-td--translation">{row.msgstr}</td>
                    <td className="vd-td vd-td--result">
                      <span className={`vd-badge vd-badge--sm ${rl.cls}`}>{rl.text}</span>
                    </td>
                    <td className="vd-td vd-td--findings">
                      {row.findings ? (
                        <div
                          className={`vd-findings${isExpanded ? ' vd-findings--expanded' : ''}`}
                          onClick={() => setExpandedRow(isExpanded ? null : idx)}
                        >
                          {isExpanded ? (
                            <div className="vd-findings__full">{row.findings}</div>
                          ) : (
                            <div className="vd-findings__preview">
                              {row.findings.length > 60
                                ? `${row.findings.substring(0, 60)}...`
                                : row.findings}
                            </div>
                          )}
                        </div>
                      ) : row.result ? (
                        <span className="vd-no-findings">No findings</span>
                      ) : (
                        <span className="vd-no-findings">Not yet verified</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="vd-td vd-empty">
                    No translations match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VerificationDetail;
